import { requestPath } from './httpFailureReporting'

/**
 * Decides whether BayaWatch session replay may run for a page load.
 *
 * ## Why
 *
 * The page routes `/reset_password/:token`, `/public/issues/:token`,
 * `/public/reports/:token` and `/public/reports/:token/issues/:issueId` carry
 * a bearer secret in the path. Up to `@bayawatch/sdk` 0.2.0 the SDK stamped
 * the raw `window.location.href` on every session-replay request, and rrweb
 * recorded it again in its Meta event and in `<a href>` attributes, so
 * recording those pages shipped the secret to watch.bayalab.com on a plain
 * visit.
 *
 * Since 0.4.0 `bayawatch.ts` passes `redactUrl` as the SDK's `sanitizeUrl`,
 * which the SDK applies to every page URL it stamps and to the URL-bearing
 * attributes rrweb records. (A request path the SDK copies into an error's
 * `context.request.url` does not go through it; `redactBayawatchEvent` scrubs
 * that.) This gate is kept as defence in depth for
 * what `sanitizeUrl` cannot see: text nodes, input values and non-URL
 * attributes are recorded as rendered (the control test in
 * `bayawatch.sessionReplay.test.ts` shows a token in page text reaching the
 * replay with the gate forced open), and the redactor knows four routes by
 * name while this rule refuses any opaque path segment.
 *
 * ## Rule
 *
 * Deny by default, using the same path redaction as error payloads: replay
 * runs only when every path segment survives `requestPath` unchanged, i.e. is
 * a lowercase resource name or a numeric id. That covers every static and
 * numeric-id route in the app today and refuses any future route that puts an
 * opaque value in the path, without a list to keep in sync with `routes.tsx`.
 *
 * ## Scope
 *
 * The decision is made once, when the SDK is initialized: the SDK cannot start
 * recording later in the same page load, and stopping it flushes one last
 * batch (its URL redacted by `sanitizeUrl` since 0.4.0). A token route is
 * therefore unrecorded when it is the page that was loaded — which is how
 * every one of them is reached (e-mail and share links; the public pages only
 * link to each other). An in-app navigation from a recorded page INTO a token
 * route keeps recording: `sanitizeUrl` redacts every URL that sends, but text
 * the token route renders would be recorded as is, which is why the native
 * deep links tear the SDK down first (`stopRecordingBeforeSecretNavigation`).
 *
 * The query string and hash are not inspected here. `sanitizeUrl`
 * (`redactUrl`) already drops query values and the fragment from every URL the
 * SDK sends; what it cannot drop is what a page renders, which is why the
 * signup section below is refused as a whole.
 *
 * ## Signup
 *
 * Every page under `/signup` is refused, static paths included. The signup
 * form (`/signup/:code`) collects a new firm's company and contact details and
 * the confirmation page (`/signup/confirm`) takes its token in the fragment,
 * which the segment rule above never sees. The privacy policy published for
 * signup promises that these screens are not recorded, so the exclusion is a
 * stated commitment, not only a token rule: it must not depend on the shape of
 * the link code.
 */
export function isReplaySafePage(href: string): boolean {
  try {
    const { pathname } = new URL(href)
    if (isSignupPath(pathname)) return false
    return requestPath(pathname) === pathname
  } catch {
    return false
  }
}

const SIGNUP_SECTION = 'signup'

let replayStarted = false

/**
 * The load-time decision of `bayawatch.ts`, kept for in-app navigation: a
 * router transition into a refused page needs to stop the SDK only when a
 * recording is actually running (see `guardRecordingAcrossNavigations`).
 */
export function setReplayStarted(started: boolean): void {
  replayStarted = started
}

export function isReplayStarted(): boolean {
  return replayStarted
}

// Neither case nor empty segments are normalized here: `requestPath` already
// refuses `/SIGNUP/...` (upper case is not a resource name) and `//signup/...`
// (it parses as a protocol-relative URL, so the path changes).
function isSignupPath(pathname: string): boolean {
  return pathname.split('/')[1] === SIGNUP_SECTION
}
